import { lexer } from 'marked';
import type { Tokens, Token } from 'marked';

import {
  isChitChatCard,
  isResearchTaskCreationCard,
  Message,
  MessageSkill,
  MessageTabs,
  MessageType,
  SearchWebPageReference,
  TaskSkill,
} from 'src/types';

export const getSkillFromMessage = (message?: Message): MessageSkill | null => {
  if (!message) {
    return null;
  }

  const { message_type, payload } = message;

  const taskSkill = isChitChatCard(payload)
    ? payload.data?.skill
    : isResearchTaskCreationCard(payload)
      ? payload.research_card?.data?.skill
      : undefined;

  switch (message_type) {
    case MessageType.SCHEDULER_TASK_CREATION_CARD:
      return MessageSkill.SCHEDULER;

    case MessageType.RESEARCH_TASK_CREATION_CARD:
      if (taskSkill === TaskSkill.DEEP_RESEARCH) {
        return MessageSkill.DEEP_RESEARCHER;
      }
      return MessageSkill.RESEARCHER;

    case MessageType.CODE_TASK_CREATION_CARD:
      return MessageSkill.CODER;

    case MessageType.CHAT_CARD:
      if (taskSkill === TaskSkill.CODING) {
        return MessageSkill.CODER;
      }
      if (taskSkill === TaskSkill.DOCUMENT_QA) {
        return MessageSkill.FILE;
      }

      return MessageSkill.WRITER;

    case MessageType.VIDEO_CARD:
      return MessageSkill.VIDEO_GENERATION;

    case MessageType.IMAGE_CARD:
      return MessageSkill.IMAGE_GENERATION;

    default:
      return MessageSkill.WRITER;
  }
};

export const getTextContentFromMessage = ({
  message,
  activeTab,
}: {
  message?: Message;
  activeTab: string;
}) => {
  if (!message) {
    return '';
  }

  const { payload } = message;

  if (isResearchTaskCreationCard(payload)) {
    if (activeTab === MessageTabs.NINJA_AGENT) {
      return (
        payload.research_card?.data?.search_result?.content?.content ||
        payload.research_card?.data?.search_result?.summary ||
        ''
      );
    }

    if (activeTab.includes(MessageTabs.SUPER_AGENT)) {
      return (
        payload.research_card?.data?.deep_research_result?.content?.content ||
        payload.research_card?.data?.deep_research_result?.summary ||
        ''
      );
    }

    const currentContent =
      Object.values(
        payload.research_card?.data?.generative_ai_result
          ?.referenced_model_summary?.references || {},
      ).find((item) => item.model === activeTab)?.content || '';

    return currentContent;
  }

  if (isChitChatCard(payload)) {
    if (activeTab === MessageTabs.NINJA_AGENT) {
      return payload.data?.ninja_model_output || '';
    }

    const currentContent =
      Object.values(
        payload.data?.referenced_model_summary?.references || {},
      ).find((item) => item.model === activeTab)?.content || '';

    return currentContent;
  }

  return '';
};

export const getWebPageReferencesFromMessage = ({
  message,
  activeTab,
}: {
  message?: Message;
  activeTab: string;
}): SearchWebPageReference[] | undefined => {
  if (!message) {
    return;
  }

  const { payload } = message;

  if (!isResearchTaskCreationCard(payload)) {
    return;
  }

  if (activeTab === MessageTabs.NINJA_AGENT) {
    return payload.research_card?.data?.search_result?.web_page_references;
  }

  if (activeTab.includes(MessageTabs.SUPER_AGENT)) {
    return payload.research_card?.data?.deep_research_result
      ?.web_page_references;
  }
};

export const getStringUrlListFromReferences = (
  references: SearchWebPageReference[],
) => {
  if (references.length === 0) {
    return '';
  }

  const urlList = references
    .map(({ url }, index) => `[${index + 1}]: ${url}`)
    .join('\n');

  return `\n\nReferences:\n${urlList}`;
};

export const getCodeContent = (updatedContent: string) => {
  const codeBlocks = updatedContent.match(/```[\s\S]*?```/g);
  const codeBlocksContent = codeBlocks
    ? codeBlocks
        .map((block) => block.replace(/```[\w-]*\n?/, '').replace(/```$/, ''))
        .join('\n')
    : '';

  return codeBlocksContent;
};

export const getHtmlContentForCopyToClipboard = (content: string) => {
  return `<!DOCTYPE html>
      <html>
        <head>
          <style>
            table {
              border-collapse: collapse;
              width: 100%;
            }
            th, td {
              border: 1px solid #ccc;
              padding: 8px;
              text-align: left;
            }
          </style>
        </head>
        <body>${content}</body>
      </html>`;
};

export const getPlainTextContentForCopyToClipboard = (
  markdown: string,
): string => {
  const tokens: Token[] = lexer(markdown);
  let result = '';

  const renderTokens = (tokens: Token[], indent = ''): void => {
    for (const token of tokens) {
      switch (token.type) {
        case 'heading': {
          const t = token as Tokens.Heading;
          result += `\n${t.depth === 1 ? '' : indent}${t.text}\n`;
          break;
        }

        case 'paragraph': {
          const t = token as Tokens.Paragraph;
          result += `\n${indent}${t.text}\n`;
          break;
        }

        case 'list': {
          const t = token as Tokens.List;
          const listContent = t.items
            .map((item: Tokens.ListItem, i) => {
              const prefix = t.ordered ? `${i + 1}.` : '•';
              return `${indent}${prefix} ${item.text}\n`;
            })
            .join('');
          result += listContent;
          break;
        }

        case 'table': {
          const t = token as Tokens.Table;

          const extractText = (cell: unknown): string => {
            if (typeof cell === 'string') {
              return cell;
            }

            if (typeof cell === 'object' && cell && 'text' in cell) {
              return String((cell as { text: string }).text);
            }

            return String(cell);
          };

          const header = t.header.map(extractText).join('\t');
          const rows = t.rows.map((row) => row.map(extractText).join('\t'));

          result += `\n${header}\n${rows.join('\n')}\n`;
          break;
        }

        case 'code': {
          const t = token as Tokens.Code;
          result += `\n${t.text}\n`;
          break;
        }

        case 'hr':
          result += `\n⸻\n`;
          break;

        case 'blockquote': {
          const t = token as Tokens.Blockquote;
          result += `\n> ${t.text}\n`;
          break;
        }

        case 'space':
          result += `\n`;
          break;

        default: {
          if ('text' in token && typeof token.text === 'string') {
            result += `\n${token.text}\n`;
          }
        }
      }
    }
  };

  renderTokens(tokens);

  return result.trim();
};
